import { Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Objective, ObjectiveProgress, ObjectiveStatus, ObjectiveType } from 'src/app/models/objective.model';
import { ObjectiveService } from 'src/app/services/objective.service';
import { ObjectiveFormComponent } from './objective-form/objective-form.component';
import { ModalController } from '@ionic/angular';
import { Chart } from 'chart.js';
import { Router } from '@angular/router';

@Component({
  selector: 'app-objective-client',
  templateUrl: './objective-client.component.html',
  styleUrls: ['./objective-client.component.scss'],
})
export class ObjectiveClientComponent implements OnInit {

  @Input() clientId: string;
  @ViewChildren('chartCanvas') chartCanvases!: QueryList<ElementRef>;

  objectives: Objective[] = [];
  progress: { [objectiveId: number]: ObjectiveProgress[] } = {};  // Stocke la progression de chaque objectif
  charts: { [key: number]: any } = {}; // Stocke les graphiques pour éviter de les recréer
  ObjectiveStatus = ObjectiveStatus


  constructor(
    private objectiveService: ObjectiveService,
    private modalCtrl: ModalController,
    private router: Router
  ) { }

  ngOnInit() {
    if (this.clientId) {
      this.loadObjectives();
    }
  }

  ngAfterViewInit() {
    this.chartCanvases.changes.subscribe(() => {
      this.chartCanvases.forEach((canvasRef, index) => {
        const objective = this.objectives[index];
        if (objective) {
          this.createChart(canvasRef.nativeElement, objective);
        }
      });
    });
    // Petit délai pour éviter les problèmes d'affichage avant que la QueryList soit remplie
    setTimeout(() => {
      this.chartCanvases.forEach((canvasRef, index) => {
        const objective = this.objectives[index];
        if (objective) {
          this.createChart(canvasRef.nativeElement, objective);
        }
      });
    }, 300);

  }

  loadObjectives() {
    this.objectiveService.objectives$.subscribe(objectives => {
      this.objectives = objectives; // Met à jour la liste dans le composant
    });
  
    // Charge les objectifs au démarrage
    this.objectiveService.getClientObjectives(this.clientId);
  }

  async openObjectiveForm() {
    try {
      const modal = await this.modalCtrl.create({
        component: ObjectiveFormComponent,
        componentProps: {
          clientId: this.clientId,
        },
      });
      modal.present();
      const { data, role } = await modal.onWillDismiss();
      if (role === 'confirm' && data) {
        this.objectiveService.addObjective(data);
      }
    } catch (error) {
      console.error('Une erreur s\'est produite lors de l\'ouverture du formulaire d\'abonnement:', error);
    }
  }

  getProgressPercentage(objectiveId: number): number {
    let objective = this.objectives.find(obj => obj.id === objectiveId);
  
    if (!objective || objective.targetValue === 0) {
      return 0; // Évite la division par zéro
    }
  
    let totalSteps = objective.targetValue; // Objectif final
    let completedSteps = objective.currentScore ?? 0; // Valeur actuelle
    let progress = 0;
  
    switch (objective.type) {
      case ObjectiveType.Progressif:
      case ObjectiveType.Frequence:
      case ObjectiveType.Duree:
      case ObjectiveType.Subjectif:
        progress = (completedSteps / totalSteps) * 100;
        break;
  
      case ObjectiveType.Degressif:
        if (!objective.startingValue) {
          return 0; // Vérifie si la valeur de départ est définie
        }
        let startingValue = objective.startingValue;
        progress = ((startingValue - completedSteps) / (startingValue - totalSteps)) * 100;
        break;
  
      case ObjectiveType.Subjectif:
        progress = (completedSteps / 10) * 100; // Normalisation sur 10
        break;
  
      default:
        progress = (completedSteps / totalSteps) * 100;
        break;
    }
  
    return Math.min(Math.max(progress, 0), 100); // Assure un pourcentage entre 0 et 100%
  }
  
  

  createChart(canvas: HTMLCanvasElement, objective: Objective) {
    const progress = this.getProgressPercentage(objective.id);
    if (this.charts[objective.id]) {
      this.charts[objective.id].destroy(); // Empêche de recréer le graphique si déjà existant
    }

    this.charts[objective.id] = new Chart(canvas, {
      type: 'doughnut',
      data: {
        datasets: [{
          data: [progress, 100 - progress],
          backgroundColor: ['#4CAF50', '#FFFFFF'], // Vert pour complété, gris pour restant
          hoverOffset: 4,
          borderWidth: 0,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          animateRotate: true,
          animateScale: true
        },
        plugins: {
          tooltip: {
            enabled: false
          }
        },
      }
    });
  }

  getStatusText(status: number): string {
    const statusMap = {
      [ObjectiveStatus.EnCours]: "En cours",
      [ObjectiveStatus.Termine]: "Terminé",
      [ObjectiveStatus.Abandonne]: "Abandonné"
    };

    return statusMap[status as ObjectiveStatus] || "Inconnu";
  }

  openObjectiveDetail(objective: Objective) {
    this.objectiveService.selectedObjective = objective;
    this.router.navigate(['/coach/objective-page']);
  }

}
